import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, CheckCircle2, ArrowLeft } from "lucide-react";
import { useAuth, formatApiError } from "@/lib/auth";
import { Logo } from "@/components/Brand";
import { useCms } from "@/lib/cms";
import PasswordStrength, { scorePassword } from "@/components/PasswordStrength";

export default function ResetPassword() {
  const { site } = useCms();
  const brandName = site?.company_name || "";
  const { token } = useParams();
  const { resetPassword } = useAuth();
  const nav = useNavigate();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [autoLogin, setAutoLogin] = useState(true);

  const strong = scorePassword(pwd) >= 3;
  const match = pwd && confirm && pwd === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!strong) { toast.error("Choose a stronger password."); return; }
    if (!match) { toast.error("Passwords don&apos;t match."); return; }
    setLoading(true);
    try {
      const res = await resetPassword(token, pwd);
      setDone(true);
      toast.success("Password reset");
      setTimeout(() => {
        if (autoLogin && res?.token) {
          nav(res.role === "admin" ? "/admin" : "/portal");
        } else {
          nav("/login");
        }
      }, 1300);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      <div className="hidden md:flex flex-col justify-between p-10 bg-[#0F172A] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(79,70,229,0.35),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(16,185,129,0.22),transparent_55%)]" />
        <div className="absolute inset-0 dot-bg opacity-30" />
        <Logo className="relative text-white [&_span]:!text-white" />
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="relative">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-400 mb-4">Password reset</div>
          <h2 className="font-display text-4xl font-semibold leading-tight">One last step. Choose something memorable, not obvious.</h2>
        </motion.div>
        <div className="relative text-xs text-gray-400">© {brandName} Institute</div>
      </div>
      <div className="flex items-center justify-center p-8">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-sm">
          <Link to="/login" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 mb-6" data-testid="reset-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
          </Link>
          {done ? (
            <div className="text-center" data-testid="reset-done">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 grid place-items-center mb-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <h1 className="font-display text-2xl font-semibold">Password updated</h1>
              <p className="text-sm text-gray-500 mt-2">Redirecting you…</p>
            </div>
          ) : (
            <form onSubmit={submit}>
              <h1 className="font-display text-3xl font-semibold">Reset your password</h1>
              <p className="text-sm text-gray-500 mt-1">Choose a new password below.</p>
              <div className="mt-6 space-y-3">
                <div className="relative">
                  <input
                    required minLength={6}
                    type={show ? "text" : "password"}
                    placeholder="New password"
                    value={pwd} onChange={(e) => setPwd(e.target.value)}
                    data-testid="reset-password"
                    className="w-full pr-10 px-3 py-3 rounded-lg border border-gray-200 focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 outline-none text-sm transition-all"
                  />
                  <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center text-gray-400 hover:text-gray-700" aria-label="Toggle password" data-testid="reset-toggle-password">
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <PasswordStrength value={pwd} />
                <input
                  required minLength={6}
                  type={show ? "text" : "password"}
                  placeholder="Confirm password"
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm"
                  className={`w-full px-3 py-3 rounded-lg border outline-none text-sm transition-all focus:ring-2 ${
                    confirm && !match
                      ? "border-rose-300 focus:border-rose-400 focus:ring-rose-100"
                      : "border-gray-200 focus:border-[#4F46E5] focus:ring-[#4F46E5]/10"
                  }`}
                />
                {confirm && !match && <div className="text-[11px] text-rose-600" data-testid="reset-mismatch">Passwords don&apos;t match.</div>}

                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                  <input
                    type="checkbox" checked={autoLogin} onChange={(e) => setAutoLogin(e.target.checked)}
                    data-testid="reset-autologin"
                    className="w-4 h-4 rounded border-gray-300 text-[#4F46E5] focus:ring-[#4F46E5]"
                  />
                  Sign me in after reset
                </label>

                <button
                  type="submit" disabled={loading || !strong || !match}
                  data-testid="reset-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#0F172A] disabled:bg-gray-300 hover:bg-[#1e293b] text-white text-sm font-medium px-4 py-3 rounded-lg transition-colors"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {loading ? "Updating…" : "Reset password"}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
}
